import {
    Component,
    OnInit,
    signal,
    computed,
    ChangeDetectorRef,
    ElementRef,
    effect,
    ChangeDetectionStrategy,
    input,
    model,
    output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { WorkflowService } from "./service/workflow.service";
import { Workflow } from "./service/Workflow";
import { WorkflowUtil } from "./workflow.util";
import { S25Util } from "../../util/s25-util";
import { WorkflowStagesComponent } from "./workflow.stages.component";
import { WorkflowStageTransitionsComponent } from "./workflow.stage.transitions.component";
import { WorkflowStageRulesStepComponent } from "./workflow.stage.rules.step.component";
import { WorkflowStatesStepComponent } from "./workflow.states.step.component";
import { WorkflowStateRulesStepComponent } from "./workflow.state.rules.step.component";
import { WorkflowActionsStepComponent } from "./workflow.actions.step.component";
import { WorkflowActionRulesStepComponent } from "./workflow.action.rules.step.component";
import { WorkflowDiagramViewerComponent } from "./diagram/workflow.diagram.viewer.component";
import { DraggableDirective } from "../s25-movable/s25.movable.element.directive";
import { NgTemplateOutlet } from "@angular/common";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { Bind } from "../../decorators/bind.decorator";
import { PreferenceService } from "../../services/preference.service";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { ApprovalGroupsStore } from "../approval-groups/approval.groups.store";
import { ExpandTrigger, WFError, WorkflowEditorInitialExpand, WorkflowSteps } from "./workflow.process.types";
import WorkflowProcess = Workflow.WorkflowProcess;

@TypeManagerDecorator("workflow-process-editor")
@Component({
    selector: "workflow-process-editor",
    template: `
        @if (sourceFormId()) {
            <div class="source-form-banner">Opened from form: {{ sourceFormName() || "Form " + sourceFormId() }}</div>
        }
        <div class="workflow-form-panel">
            <div class="workflow-steps">
                <!-- Step 1: Basic Info -->
                @let basicStep = workflowSteps.Basic;
                @let basicStepSelected = isCurrentStep(basicStep);
                <div class="step" [class.active]="basicStepSelected">
                    <div class="step-header" (click)="toggleStep(basicStep)">
                        <span class="step-number">{{ basicStep }}</span>
                        <span class="step-title">Basic Info</span>
                        <span class="step-required">Required</span>
                        <span class="step-status" [class.complete]="isStepComplete(basicStep)">✓</span>
                        <span class="step-arrow" [class.expanded]="basicStepSelected">▶</span>
                    </div>
                    @if (basicStepSelected) {
                        <div class="step-content">
                            <div class="form-row">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    class="c-input"
                                    [ngModel]="workflowProcess().workflowProcessName"
                                    placeholder="Enter workflow name"
                                    [class.error-input]="errors().basic?.message"
                                    (ngModelChange)="updateWorkflowName($event)"
                                />
                                @if (errors().basic?.message) {
                                    <p class="ngRed c-margin-top--quarter">{{ errors().basic.message }}</p>
                                }
                            </div>
                            <div class="form-row">
                                <label>Description</label>
                                <textarea
                                    class="c-input"
                                    [(ngModel)]="workflowProcess().description"
                                    placeholder="Optional description"
                                    rows="2"
                                ></textarea>
                            </div>
                            <div class="form-row">
                                <s25-ng-checkbox [(value)]="workflowProcess().enabled"> Enabled </s25-ng-checkbox>
                            </div>
                        </div>
                    }
                </div>

                <!-- Step 2: Stages -->
                @let stageStep = workflowSteps.Stages;
                @let stageStepSelected = isCurrentStep(stageStep);
                <div class="step" [class.active]="stageStepSelected">
                    <div class="step-header" (click)="toggleStep(stageStep)">
                        <span class="step-number">{{ stageStep }}</span>
                        <span class="step-title">Stages</span>
                        <span class="step-required">Required</span>
                        <span class="step-status" [class.complete]="isStepComplete(stageStep)">✓</span>
                        <span class="step-arrow" [class.expanded]="stageStepSelected">▶</span>
                    </div>
                    @let stagesSummary = WorkflowUtil.getStagesSummary(workflowProcess());
                    @if (stagesSummary.length && !stageStepSelected) {
                        <div class="step-summary">
                            @for (stage of stagesSummary; track stage.id) {
                                <span class="summary-tag clickable" (click)="expandPreviewItem(stageStep, stage.id)">{{
                                    stage.name
                                }}</span>
                            }
                        </div>
                    }
                    @if (stageStepSelected) {
                        <div class="step-content">
                            <workflow-stages
                                [workflowProcess]="workflowProcess()"
                                [error]="errors().stages"
                                [(selectedStageTrigger)]="expandTrigger"
                                (workflowProcessChange)="onWorkflowProcessChange($event)"
                            ></workflow-stages>
                            <div class="nested-rules-section">
                                <workflow-stage-rules-step
                                    [workflowProcess]="workflowProcess()"
                                    [(selectedStageTrigger)]="expandTrigger"
                                ></workflow-stage-rules-step>
                            </div>
                        </div>
                    }
                </div>

                <!-- Step 3: Approvals -->
                @let statesStep = workflowSteps.States;
                @let statesStepSelected = isCurrentStep(statesStep);
                <div class="step" [class.active]="statesStepSelected">
                    <div class="step-header" (click)="toggleStep(statesStep)">
                        <span class="step-number">{{ statesStep }}</span>
                        <span class="step-title">Approvals</span>
                        <span class="step-status" [class.complete]="isStepComplete(statesStep)">✓</span>
                        <span class="step-arrow" [class.expanded]="statesStepSelected">▶</span>
                    </div>
                    @let statesSummary = WorkflowUtil.getStatesSummary(workflowProcess());
                    @if (statesSummary.length && !statesStepSelected) {
                        <div class="step-summary">
                            @for (state of statesSummary; track state.id) {
                                <span class="summary-tag clickable" (click)="expandPreviewItem(statesStep, state.id)">{{
                                    state.name
                                }}</span>
                            }
                        </div>
                    }
                    @if (statesStepSelected) {
                        <div class="step-content">
                            <workflow-states-step
                                [workflowProcess]="workflowProcess()"
                                [error]="errors().states"
                                [aiFeatureEnabled]="aiFeatureEnabled()"
                                [(selectedStateTrigger)]="expandTrigger"
                                (workflowProcessChange)="onWorkflowProcessChange($event)"
                            ></workflow-states-step>
                            <div class="nested-rules-section">
                                <workflow-state-rules-step
                                    [workflowProcess]="workflowProcess()"
                                    [(selectedStateTrigger)]="expandTrigger"
                                ></workflow-state-rules-step>
                            </div>
                        </div>
                    }
                </div>

                <!-- Step 4: Actions -->
                @let actionStep = workflowSteps.Actions;
                @let actionStepSelected = isCurrentStep(actionStep);
                <div class="step" [class.active]="actionStepSelected">
                    <div class="step-header" (click)="toggleStep(actionStep)">
                        <span class="step-number">{{ actionStep }}</span>
                        <span class="step-title">Actions</span>
                        <span class="step-status" [class.complete]="isStepComplete(actionStep)">✓</span>
                        <span class="step-arrow" [class.expanded]="actionStepSelected">▶</span>
                    </div>
                    @let actionsSummary = WorkflowUtil.getActionsSummary(workflowProcess());
                    @if (actionsSummary.length && !actionStepSelected) {
                        <div class="step-summary">
                            @for (action of actionsSummary; track action.id) {
                                <span
                                    class="summary-tag clickable"
                                    (click)="expandPreviewItem(actionStep, action.id, sourceFormId())"
                                    >{{ action.name }}</span
                                >
                            }
                        </div>
                    }
                    @if (actionStepSelected) {
                        <div class="step-content">
                            <workflow-actions-step
                                [(workflowProcess)]="workflowProcess"
                                [error]="errors().actions"
                                [(selectedActionTrigger)]="expandTrigger"
                            ></workflow-actions-step>
                            <div class="nested-rules-section">
                                <workflow-action-rules-step
                                    [workflowProcess]="workflowProcess()"
                                    [(selectedStageTrigger)]="expandTrigger"
                                ></workflow-action-rules-step>
                            </div>
                        </div>
                    }
                </div>

                <!-- Step 5: Transitions -->
                @let transitionStep = workflowSteps.Transitions;
                @let transitionStepSelected = isCurrentStep(transitionStep);
                <div class="step" [class.active]="transitionStepSelected">
                    <div class="step-header" (click)="toggleStep(transitionStep)">
                        <span class="step-number">{{ transitionStep }}</span>
                        <span class="step-title">Transitions</span>
                        <span class="step-required">Required</span>
                        <span class="step-status" [class.complete]="isStepComplete(transitionStep)">✓</span>
                        <span class="step-arrow" [class.expanded]="transitionStepSelected">▶</span>
                    </div>
                    @let transitionsSummary = WorkflowUtil.getTransitionsSummary(workflowProcess());
                    @if (transitionsSummary.length && !transitionStepSelected) {
                        <div class="step-summary">
                            @for (transition of transitionsSummary; track transition.id) {
                                <span
                                    class="summary-tag clickable"
                                    (click)="expandPreviewItem(transitionStep, transition.id)"
                                    >{{ transition.name }}</span
                                >
                            }
                        </div>
                    }
                    @if (transitionStepSelected) {
                        <div class="step-content">
                            <workflow-stage-transitions
                                [workflowProcess]="workflowProcess()"
                                [error]="errors().transitions"
                                [(selectedTransitionTrigger)]="expandTrigger"
                                (transitionsChange)="onStageTransitionsChange($event)"
                            ></workflow-stage-transitions>
                        </div>
                    }
                </div>
                <ng-container *ngTemplateOutlet="editorActions"></ng-container>
            </div>
        </div>
        <ng-template #editorActions>
            <div class="actions">
                @if (!embedded()) {
                    <button class="aw-button aw-button--outline" (click)="toggleDiagram()">
                        {{ showDiagram() ? "Hide" : "Show" }} Diagram
                    </button>
                }
                <button class="aw-button aw-button--danger--outline" (click)="cancelEdit()">Cancel</button>
                <s25-ng-button [type]="'primary'" [onClick]="saveWorkflow">{{
                    isNewWorkflow() ? "Create" : "Save"
                }}</s25-ng-button>
            </div>
        </ng-template>
        @if (showDiagram() && !embedded()) {
            <div draggableElement [fitToViewport]="true" class="workflow-diagram-panel">
                <workflow-diagram-viewer
                    [workflowProcess]="workflowForDiagram()"
                    [(showDiagram)]="showDiagram"
                ></workflow-diagram-viewer>
            </div>
        }
    `,
    styles: `
        .source-form-banner {
            padding: 10px 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            margin-bottom: 12px;
        }
        .nested-rules-section {
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px dashed var(--color--border);
        }
        .workflow-form-panel {
            flex: 1;
            min-width: 0;
            overflow-y: auto;
            overflow-x: hidden;
        }
        .workflow-steps {
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 20px;
            max-width: 100%;
        }
        .step {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }
        .step.active {
            border-color: var(--color--link-text);
        }
        .step-header {
            padding: 16px 20px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 12px;
            transition: background var(--transition-medium);
        }
        .step-header:hover {
            background: var(--color--surface-2-bg-hover);
        }
        .step-number {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: var(--color--border-bold);
            color: var(--color--surface-bg);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
            flex-shrink: 0;
        }
        .step.active .step-number {
            background: var(--color--link-text);
        }
        .step-title {
            font-weight: 500;
            color: var(--color--text);
        }
        .step-required {
            padding: 2px 8px;
            background: var(--color--warning);
            color: var(--color--surface-bg);
            border-radius: 10px;
            font-size: 11px;
            font-weight: 500;
            margin-right: auto;
        }
        .step-status {
            opacity: 0;
            color: var(--color--registration);
            font-weight: bold;
        }
        .step-status.complete {
            opacity: 1;
            margin-left: auto;
        }
        .step-status:not(.complete) ~ .step-arrow {
            margin-left: auto;
        }
        .step-arrow {
            color: var(--color--text-muted);
            font-size: 12px;
            transition: transform var(--transition-medium);
        }
        .step-arrow.expanded {
            transform: rotate(90deg);
        }
        .step-content {
            padding: 20px;
        }
        .step-summary {
            padding: 12px 20px;
            background: var(--color--surface-bg);
            border-top: 1px solid var(--color--border);
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }
        .summary-tag {
            padding: 4px 10px;
            background: var(--color--surface-2-bg);
            border-radius: 12px;
            font-size: 12px;
            color: var(--color--text-muted);
        }
        .summary-tag.clickable {
            cursor: pointer;
            transition: background var(--transition-medium);
        }
        .summary-tag.clickable:hover {
            background: var(--color--surface-2-bg-hover);
        }
        .form-row {
            margin-bottom: 1.25rem;
        }
        .form-row:last-child {
            margin-bottom: 0;
        }
        .form-row label {
            display: block;
            margin-bottom: 0.625rem;
            font-weight: 500;
            color: var(--color--text-muted);
        }
        .form-row input.c-input,
        .form-row textarea.c-input {
            display: block;
            width: 100%;
            box-sizing: border-box;
        }
        .form-row .error-input {
            border-bottom: 2px solid var(--color--danger);
        }
        .actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 12px;
            margin-top: 8px;
            padding-top: 16px;
        }

        .actions > .aw-button,
        .actions > s25-ng-button {
            margin-left: 0;
        }

        .actions > .aw-button,
        .actions > s25-ng-button ::ng-deep .aw-button {
            border-radius: var(--border-radius);
        }
        .workflow-diagram-panel {
            height: auto;
            display: flex;
            flex-direction: column;
            overflow: auto;
        }
    `,
    standalone: true,
    imports: [
        FormsModule,
        WorkflowStagesComponent,
        WorkflowStageTransitionsComponent,
        WorkflowStageRulesStepComponent,
        WorkflowStatesStepComponent,
        WorkflowStateRulesStepComponent,
        WorkflowActionsStepComponent,
        WorkflowActionRulesStepComponent,
        WorkflowDiagramViewerComponent,
        DraggableDirective,
        NgTemplateOutlet,
        S25ButtonComponent,
        S25CheckboxModule,
    ],
    providers: [ApprovalGroupsStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowProcessEditorComponent implements OnInit {
    workflowProcess = model.required<Workflow.WorkflowProcess>();
    workflowProcessId = input<number | null>(null);
    embedded = input(false);
    sourceFormId = input<number | undefined>();
    sourceFormName = input<string | undefined>();
    initialExpand = input<WorkflowEditorInitialExpand | undefined>();
    saved = output<Workflow.WorkflowProcess>();
    cancelled = output<void>();

    stageRulesPreviewTags = signal<{ id: number; name: string }[]>([]);
    currentStep = signal<WorkflowSteps | null>(WorkflowSteps.Basic);
    showDiagram = signal(false);
    errors = signal<Record<string, WFError>>({});
    expandTrigger = signal<ExpandTrigger>(undefined);
    aiFeatureEnabled = signal(false);

    workflowForDiagram = computed(() => {
        const workflow = this.workflowProcess();
        if (!workflow) return null;
        const copy = {
            ...workflow,
            stages: workflow.stages ? [...workflow.stages] : [],
            stageTransitions: workflow.stageTransitions ? [...workflow.stageTransitions] : [],
        };
        if (copy.stageTransitions && copy.stages)
            WorkflowUtil.populateStageNamesOnTransitions(copy.stageTransitions, copy.stages);
        return copy;
    });

    protected readonly WorkflowUtil = WorkflowUtil;
    isNewWorkflow = computed(() => !this.workflowProcessId());
    private hasErrors = computed(() => !!Object.keys(this.errors()).length);
    private readonly workflowStepCompletion = computed(() => {
        const workflow = this.workflowProcess();
        return {
            [WorkflowSteps.Basic]: WorkflowUtil.isWorkflowStepComplete(workflow, WorkflowSteps.Basic),
            [WorkflowSteps.Stages]: WorkflowUtil.isWorkflowStepComplete(workflow, WorkflowSteps.Stages),
            [WorkflowSteps.States]: WorkflowUtil.isWorkflowStepComplete(workflow, WorkflowSteps.States),
            [WorkflowSteps.Actions]: WorkflowUtil.isWorkflowStepComplete(workflow, WorkflowSteps.Actions),
            [WorkflowSteps.Transitions]: WorkflowUtil.isWorkflowStepComplete(workflow, WorkflowSteps.Transitions),
        };
    });
    protected isCurrentStep = (step: WorkflowSteps) => step === this.currentStep();
    protected workflowSteps = WorkflowSteps;

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        effect(() => {
            if (this.hasErrors()) this.el.nativeElement.querySelector(".expanded")?.scrollIntoView(false);
        });
        effect(() => {
            this.workflowProcess();
            void this.refreshStageRulesPreview();
        });
        effect(() => {
            const initial = this.initialExpand();
            if (!initial) return;
            this.currentStep.set(initial.step);
            this.currentStep.set(initial.step);
            this.expandTrigger.set({
                step: initial.step,
                id: initial.actionId ?? 0,
                formId: initial.formId,
            });
        });
    }

    async ngOnInit() {
        try {
            const prefs = await PreferenceService.getPreferences(["config_ai_workflow_review"], "S");
            this.aiFeatureEnabled.set(prefs?.config_ai_workflow_review?.value === "T");
        } catch {}
    }

    async refreshStageRulesPreview(): Promise<void> {
        const workflow = this.workflowProcess();
        if (!workflow?.workflowProcessId) {
            this.stageRulesPreviewTags.set([]);
            return;
        }
        const pid = workflow.workflowProcessId;
        const stages = await WorkflowUtil.loadStagesWithWorkflowRules(workflow);
        if (this.workflowProcess()?.workflowProcessId !== pid) return;
        this.stageRulesPreviewTags.set(stages.map((s) => ({ id: s.workflowStageId!, name: s.stageName })));
    }

    toggleStep(step: number) {
        const previous = this.currentStep();
        if (this.currentStep() === step) this.currentStep.set(null);
        else this.currentStep.set(step);
        if (previous === WorkflowSteps.Stages && this.currentStep() !== WorkflowSteps.Stages)
            void this.refreshStageRulesPreview();
    }

    toggleDiagram() {
        this.showDiagram.update((v) => !v);
    }
    cancelEdit() {
        this.errors.set({});
        this.cancelled.emit();
    }

    isStepComplete(step: WorkflowSteps): boolean {
        return this.workflowStepCompletion()[step];
    }

    @Bind async saveWorkflow() {
        const workflow = this.workflowProcess();
        if (!workflow?.workflowProcessName?.trim()) {
            this.setError("Workflow name is required", WorkflowSteps.Basic);
            return;
        }
        const fieldRefErrors = await WorkflowUtil.validateWorkflowBeforeSave(workflow);
        if (fieldRefErrors.length) {
            this.setError(
                `<p>Some workflow action issues must be resolved before saving:</p><ul>${fieldRefErrors.map((e) => `<li>${e}</li>`).join("")}</ul>`,
                WorkflowSteps.Actions,
            );
            return;
        }
        if (!this.validateWorkflowStructure(workflow)) return;
        try {
            if (this.isNewWorkflow()) await WorkflowService.createWorkflowProcess(workflow);
            else await WorkflowService.updateWorkflowProcess(workflow.workflowProcessId!, workflow);
            this.saved.emit(workflow);
        } catch (error) {
            S25Util.showError(error);
        }
    }

    onWorkflowProcessChange(updated: Workflow.WorkflowProcess) {
        updated.stages?.forEach((stage, index) => {
            if (!stage.id) stage.id = stage.workflowStageId || -(index + 1);
        });
        this.workflowProcess.set({ ...updated });
    }

    onStageTransitionsChange(transitions: Workflow.WorkflowStageTransition[]) {
        const workflow = this.workflowProcess();
        if (!workflow) return;
        transitions.forEach((t, index) => {
            if (!t.id) t.id = t.workflowStageTransitionId || -(index + 1000);
        });
        this.workflowProcess.set({ ...workflow, stageTransitions: [...transitions] });
    }

    updateWorkflowName(value: string) {
        this.workflowProcess.update((wf) => <WorkflowProcess>{ ...wf, workflowProcessName: value });
    }
    expandPreviewItem(step: WorkflowSteps, id: number, formId?: number | null) {
        this.currentStep.set(step);
        this.expandTrigger.set({ step, id, formId });
    }

    setError(message: string, step: WorkflowSteps, indices?: number[]) {
        const map: Partial<Record<WorkflowSteps, string>> = {
            [WorkflowSteps.Basic]: "basic",
            [WorkflowSteps.Stages]: "stages",
            [WorkflowSteps.States]: "states",
            [WorkflowSteps.Actions]: "actions",
            [WorkflowSteps.Transitions]: "transitions",
        };
        const prop = map[step];
        if (prop) {
            this.errors.set({ [prop]: <WFError>{ message, indices: indices ?? [] } });
            this.currentStep.set(step);
        }
    }

    private validateWorkflowStructure(workflow: Workflow.WorkflowProcess): boolean {
        this.errors.set({});
        const validationError = WorkflowUtil.validateWorkflowStructure(workflow);
        if (validationError) {
            this.setError(validationError.message, validationError.step, validationError.indices);
            return false;
        }
        return true;
    }
}
